import { Component, Host, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';

import { LocalizationService } from '@progress/kendo-angular-l10n';

import { Orientation } from '../common/orientation';
import { DraggableDirective } from '../common/draggable.directive';
import { Keys } from '../common/keys';

import { SplitterService, SplitterDragState } from './splitter.service';

import { Subscription, of } from 'rxjs';
import { delay, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

const stopPropagation = ({ originalEvent: event }) => {
  event.stopPropagation();
  event.preventDefault();
};

const toggleContentOverlay = (splitter, index, toggle) => {
  splitter.pane(index).overlayContent = toggle;
  splitter.pane(index + 1).overlayContent = toggle;
  if (!toggle) {
    splitter.pane(index).cd.detectChanges();
    splitter.pane(index + 1).cd.detectChanges();
  }
};

const preventOnDblClick = (release) => (mouseDown) => of(mouseDown).pipe(delay(150), takeUntil(release));

const classFromObject = (classes) =>
  Object.keys(classes)
    .filter((c) => classes[c])
    .join(' ');

const createMoveStream = (draggable) => (mouseDown) =>
  draggable.kendo.drag.pipe(
    takeUntil(draggable.kendo.release),
    map(({ pageX, pageY }) => ({
      originalX: mouseDown.pageX,
      originalY: mouseDown.pageY,
      pageX,
      pageY,
    })),
  );

/**
 * @hidden
 */
@Component({
  standalone: false,
  selector: 'kendo-splitter-bar',
  template: `
    <div [class]="previousArrowClass()" (click)="togglePrevious()"></div>
    <div class="k-resize-handle"></div>
    <div [class]="nextArrowClass()" (click)="toggleNext()"></div>
  `,
})
export class SplitterBarComponent implements OnInit, OnDestroy {
  @Input()
  @HostBinding('attr.aria-orientation')
  public orientation: Orientation = 'horizontal';

  public get direction(): string {
    return this.localization.rtl ? 'rtl' : 'ltr';
  }

  @Input()
  public index: number = 0;

  @HostBinding('attr.role')
  public ariaRole: string = 'separator';

  @HostBinding('attr.tabindex')
  public get tabIndex(): number {
    return this.splitter.isStatic(this.index) ? -1 : 0;
  }

  @HostBinding('class')
  public get hostClasses(): string {
    const isHorizontal = this.orientation === 'horizontal';
    const isDraggable = this.splitter.isDraggable(this.index);
    const isStatic = this.splitter.isStatic(this.index);

    return classFromObject({
      'k-state-focused': this.focused,
      'k-splitbar': true,
      'k-splitbar-horizontal': isHorizontal,
      'k-splitbar-vertical': !isHorizontal,
      'k-splitbar-draggable-horizontal': isHorizontal && isDraggable,
      'k-splitbar-draggable-vertical': !isHorizontal && isDraggable,
      'k-splitbar-static-horizontal': isHorizontal && isStatic,
      'k-splitbar-static-vertical': !isHorizontal && isStatic,
    });
  }

  @HostBinding('class.k-state-focused')
  public focused: boolean = false;

  @HostBinding('style.-ms-flex-order')
  @HostBinding('style.order')
  public get order(): number {
    return 2 * this.index + 1;
  }

  @HostListener('dblclick')
  public collapseAny(): void {
    this.tryToggleNearest();
  }

  @HostListener('focusin')
  public onFocusIn(): void {
    this.focused = true;
  }

  @HostListener('focusout')
  public onFocusOut(): void {
    this.focused = false;
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: any): void {
    const keyCode = event && event.keyCode;
    const isHorizontal = this.orientation === 'horizontal';

    const resize = (delta) => {
      event.preventDefault();
      const state = this.splitter.dragState(this.index);

      // resizing next pane, reverse delta
      if (state.index === this.index + 1) {
        delta *= -1;
      }

      this.splitter.setSize(state, delta);
    };

    if (keyCode === Keys.enter) {
      event.preventDefault();
      this.tryToggleNearest();
    } else if (isHorizontal && keyCode === Keys.left) {
      resize(-10);
    } else if (isHorizontal && keyCode === Keys.right) {
      resize(10);
    } else if (!isHorizontal && keyCode === Keys.up) {
      resize(-10);
    } else if (!isHorizontal && keyCode === Keys.down) {
      resize(10);
    }
  }

  constructor(
    @Host() public draggable: DraggableDirective,
    private splitter: SplitterService,
    private localization: LocalizationService,
  ) {}

  public ngOnInit(): void {
    let state: SplitterDragState;

    const listener = this.draggable.kendo.press
      .pipe(
        tap(stopPropagation),
        filter(() => this.splitter.isDraggable(this.index)),
        tap(() => (state = this.splitter.dragState(this.index))),
        tap(() => toggleContentOverlay(this.splitter, this.index, true)),
        switchMap(preventOnDblClick(this.draggable.kendo.release)),
        switchMap(createMoveStream(this.draggable)),
      )
      .subscribe(({ pageX, pageY, originalX, originalY }) => {
        let delta;

        if (this.orientation === 'vertical') {
          delta = pageY - originalY;
        } else if (this.direction === 'rtl') {
          delta = originalX - pageX;
        } else {
          delta = pageX - originalX;
        }

        // resizing next pane, reverse delta
        if (state.index === this.index + 1) {
          delta *= -1;
        }

        this.splitter.setSize(state, delta);
      });

    this.subscriptions.add(listener);

    this.subscriptions.add(
      this.draggable.kendo.release.subscribe(() => toggleContentOverlay(this.splitter, this.index, false)),
    );
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  public togglePrevious(): void {
    this.splitter.tryToggle(this.index);
  }

  public toggleNext(): void {
    this.splitter.tryToggle(this.index + 1);
  }

  public previousArrowClass(): string {
    const pane = this.splitter.pane(this.index);
    const isCollapsible = pane.collapsible;
    const isCollapsed = pane.collapsed;
    const isHorizontal = this.orientation === 'horizontal';

    return classFromObject({
      fa: true,
      'k-hidden': !isCollapsible,
      'k-collapse-prev': isCollapsible,
      'fa-circle-arrow-left': isCollapsible && isHorizontal && !isCollapsed,
      'fa-circle-arrow-right': isCollapsible && isHorizontal && isCollapsed,
      'fa-circle-arrow-up': isCollapsible && !isHorizontal && !isCollapsed,
      'fa-circle-arrow-down': isCollapsible && !isHorizontal && isCollapsed,
    });
  }

  public nextArrowClass(): string {
    const pane = this.splitter.pane(this.index + 1);
    const isCollapsible = pane.collapsible;
    const isCollapsed = pane.collapsed;
    const isHorizontal = this.orientation === 'horizontal';

    return classFromObject({
      fa: true,
      'k-hidden': !isCollapsible,
      'k-collapse-next': isCollapsible,
      'fa-circle-arrow-right': isCollapsible && isHorizontal && !isCollapsed,
      'fa-circle-arrow-left': isCollapsible && isHorizontal && isCollapsed,
      'fa-circle-arrow-down': isCollapsible && !isHorizontal && !isCollapsed,
      'fa-circle-arrow-up': isCollapsible && !isHorizontal && isCollapsed,
    });
  }

  private subscriptions: Subscription = new Subscription();

  private tryToggleNearest(): void {
    const prev = this.index;
    const next = this.index + 1;

    if (!this.splitter.tryToggle(prev)) {
      this.splitter.tryToggle(next);
    }
  }
}
